import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

function ShiftTypesPanel({ user, token }) {
    const [shiftTypes, setShiftTypes] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [formData, setFormData] = useState({
        type_name: '',
        type_code: '',
        pay_coefficient: 1.0,
        description: '',
        is_active: true
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const canEdit = ['owner', 'admin'].includes(user?.role);

    useEffect(() => {
        fetchShiftTypes();
    }, []);

    const fetchShiftTypes = async () => {
        try {
            const response = await fetch(`${API_URL}/api/shift-types`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setShiftTypes(data.data);
            }
        } catch (error) {
            console.error('Ошибка при загрузке типов смен:', error);
        }
    };

    const handleOpenDialog = (shiftType = null) => {
        if (shiftType) {
            setEditingType(shiftType);
            setFormData({
                type_name: shiftType.type_name,
                type_code: shiftType.type_code,
                pay_coefficient: shiftType.pay_coefficient,
                description: shiftType.description || '',
                is_active: shiftType.is_active
            });
        } else {
            setEditingType(null);
            setFormData({
                type_name: '',
                type_code: '',
                pay_coefficient: 1.0,
                description: '',
                is_active: true
            });
        }
        setOpenDialog(true);
        setError('');
        setSuccess('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingType(null);
    };

    const handleSubmit = async () => {
        if (!formData.type_name || !formData.type_code) {
            setError('Заполните все обязательные поля');
            return;
        }

        if (formData.pay_coefficient <= 0) {
            setError('Коэффициент должен быть больше 0');
            return;
        }

        try {
            const url = editingType
                ? `${API_URL}/api/shift-types/${editingType.id}`
                : `${API_URL}/api/shift-types`;

            const response = await fetch(url, {
                method: editingType ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!data.success) {
                setError(data.error);
                return;
            }

            setSuccess(editingType ? 'Тип смены обновлен' : 'Тип смены создан');
            setTimeout(() => {
                handleCloseDialog();
                fetchShiftTypes();
            }, 1000);

        } catch (error) {
            setError('Ошибка при сохранении');
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <Typography variant="h4">
                    <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Типы смен
                </Typography>
            </Box>

            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Таблица типов смен */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Название</strong></TableCell>
                            <TableCell><strong>Код</strong></TableCell>
                            <TableCell align="right"><strong>Коэффициент</strong></TableCell>
                            <TableCell><strong>Описание</strong></TableCell>
                            <TableCell align="center"><strong>Статус</strong></TableCell>
                            {canEdit && <TableCell align="center"><strong>Действия</strong></TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shiftTypes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        Нет типов смен
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            shiftTypes.map((type) => (
                                <TableRow key={type.id} hover>
                                    <TableCell>
                                        <Typography fontWeight={type.is_active ? 'bold' : 'normal'}>
                                            {type.type_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={type.type_code} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={`x${type.pay_coefficient}`}
                                            color={type.pay_coefficient > 1.0 ? 'warning' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {type.description || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={type.is_active ? 'Активен' : 'Неактивен'}
                                            color={type.is_active ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    {canEdit && (
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleOpenDialog(type)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Информация */}
            <Paper sx={{ mt: 3, p: 2, bgcolor: 'info.light' }}>
                <Typography variant="body2" color="white">
                    <strong>Коэффициенты оплаты:</strong>
                </Typography>
                <Typography variant="body2" color="white">
                    • x1.0 - обычная смена (без надбавок)
                </Typography>
                <Typography variant="body2" color="white">
                    • x1.5 - смена с повышенным коэффициентом (вечерняя, выходная)
                </Typography>
                <Typography variant="body2" color="white">
                    • x2.0 - ночная смена или праздничный день
                </Typography>
                <Typography variant="body2" color="white" sx={{ mt: 1 }}>
                    Коэффициент умножается на базовую ставку за час работы.
                </Typography>
            </Paper>

            {/* Диалог создания/редактирования */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingType ? 'Редактировать тип смены' : 'Новый тип смены'}
                </DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                    <TextField
                        fullWidth
                        label="Название типа *"
                        value={formData.type_name}
                        onChange={(e) => setFormData({ ...formData, type_name: e.target.value })}
                        margin="normal"
                        disabled={!!editingType}
                    />

                    <TextField
                        fullWidth
                        label="Код типа *"
                        value={formData.type_code}
                        onChange={(e) => setFormData({ ...formData, type_code: e.target.value })}
                        margin="normal"
                        disabled={!!editingType}
                        helperText="Латинские буквы, без пробелов (например: evening, night)"
                    />

                    <TextField
                        fullWidth
                        label="Коэффициент оплаты *"
                        type="number"
                        value={formData.pay_coefficient}
                        onChange={(e) => setFormData({ ...formData, pay_coefficient: e.target.value })}
                        margin="normal"
                        inputProps={{ min: 0.1, step: 0.1 }}
                        helperText="Множитель к базовой ставке (1.0 = без надбавок)"
                    />

                    <TextField
                        fullWidth
                        label="Описание"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        margin="normal"
                        multiline
                        rows={2}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            />
                        }
                        label="Активен (доступен для использования)"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Отмена</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={!formData.type_name || !formData.type_code}
                    >
                        {editingType ? 'Сохранить' : 'Создать'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ShiftTypesPanel;
